/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useMemo } from 'react';
import { useCircles, useVerticals } from '@/queries/session';
import { useBoardTasks } from '@/queries/tasks';
import { PRIORITIES, verticalColorVar } from '@/types/task';
import { useBoardFilters } from './useBoardFilters';
import { X } from 'lucide-react';

type Chip = { key: string; kind: string; label: string; colour?: string; off: () => void };

/**
 * What is filtering the board, on the bar instead of behind it.
 *
 * **The problem this solves is already written down two files away.**
 * `BoardControls` keeps a count badge on the Filters button with the comment
 * *"a hidden filter is how a board ends up looking empty for no visible
 * reason"* — which is right, and a number is a thin answer to it. It tells you
 * that something is filtering, never what. To find out you have to open a
 * popover, read four selects, and close it again.
 *
 * So the filters that are on are named here, and each one can be taken off where
 * it is named. The popover stays for *choosing*; this row is for *knowing*.
 *
 * **The search term is deliberately not a chip.** It already has a control of its
 * own on this bar with the text sitting in it, and a second copy two centimetres
 * away reads as a second filter rather than the same one. Everything here is a
 * filter with no other representation on screen.
 *
 * **The selects keep their ids.** Nothing about this replaces them, so the
 * fourteen `selectOption` assertions the browser suite drives them with are
 * untouched — this is additive, and it can ship long before they are reworked.
 */
export function ActiveFilterChips() {
  const { data: circles } = useCircles();
  const { filters, setFilter } = useBoardFilters();
  const columns = useVerticals();
  const { data: tasks } = useBoardTasks(circles?.activeId ?? '', circles !== undefined);

  // Tags are filtered by id and named by tasks, so the label has to be looked
  // up. Falling back to the id is deliberate: a tag removed from every task
  // while its filter is still on would otherwise render a chip with no text —
  // an × floating on the bar with nothing beside it.
  const tagName = useMemo(() => {
    if (!filters.tag) return '';
    for (const t of tasks ?? []) for (const g of t.tags) if (g.id === filters.tag) return g.name;
    return filters.tag;
  }, [tasks, filters.tag]);

  const vertical = columns.find((v) => v.id === filters.vertical);

  // Built by pushing rather than by filtering a sparse literal: `x && {…}` on a
  // string field widens to `'' | {…}`, which `NonNullable` does not narrow, and
  // every field access downstream then fails to compile.
  const chips: Chip[] = [];
  if (vertical) {
    chips.push({
      key: 'vertical',
      kind: 'vertical',
      label: vertical.label,
      colour: verticalColorVar(vertical.color || vertical.id),
      off: () => setFilter({ vertical: '' }),
    });
  }
  if (filters.owner) {
    chips.push({
      key: 'owner',
      kind: 'participant',
      label: filters.owner,
      off: () => setFilter({ owner: '' }),
    });
  }
  if (filters.tag) {
    chips.push({ key: 'tag', kind: 'tag', label: tagName, off: () => setFilter({ tag: '' }) });
  }
  if (filters.priority) {
    const p = PRIORITIES.find((x) => String(x.id) === filters.priority);
    chips.push({
      key: 'priority',
      kind: 'priority',
      label: p ? `${p.label} — ${p.name}` : filters.priority,
      off: () => setFilter({ priority: '' }),
    });
  }
  if (filters.overdue) {
    chips.push({ key: 'overdue', kind: 'overdue', label: 'Overdue', off: () => setFilter({ overdue: false }) });
  }

  // Nothing on: no row at all, rather than an empty strip holding height. The
  // bar should not get taller for a state that has nothing to say.
  if (chips.length === 0) return null;

  return (
    <div
      className="active-filters flex flex-wrap items-center gap-2 border-t border-border/70 py-2"
      // Named for a screen reader, because visually this row is understood by
      // being next to the board it is narrowing.
      aria-label="Active filters"
    >
      <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">Filtered by</span>

      {chips.map((c) => (
        <span
          key={c.key}
          className="filter-chip motion-safe:animate-[rise_0.22s_var(--ease-out-soft)_both] flex items-center gap-1.5 rounded-lg border border-border bg-surface py-1 pl-2 pr-1 text-xs font-semibold text-ink"
        >
          {c.colour && (
            <span className="size-2 shrink-0 rounded-full" style={{ background: c.colour }} aria-hidden />
          )}
          <span className="max-w-[180px] truncate">{c.label}</span>
          <button
            type="button"
            onClick={c.off}
            aria-label={`Remove ${c.kind} filter: ${c.label}`}
            className="tap grid size-[18px] shrink-0 place-items-center rounded text-ink-faint transition-colors duration-fast ease-out-soft hover:bg-bg hover:text-ink"
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}

      {/* **No "Clear all" here.** There was one, and putting it on screen beside
          the existing `#board-clear` — which is four inches to the right on the
          same bar and does exactly this — made two controls for one action.
          The split is cleaner without it: a chip takes off the one filter it
          names, and Clear takes off all of them. */}
    </div>
  );
}
